import React from 'react';

const STORAGE_KEY = 'tradournal_watchlist';
const ALERTS_KEY = 'tradournal_alerts';

function loadStorage(key, fallback) {
  try { return JSON.parse(localStorage.getItem(key)) || fallback; } catch { return fallback; }
}

function toNum(v, fb = 0) {
  const n = parseFloat(v);
  return isFinite(n) ? n : fb;
}

export default function WatchlistPanel({ C, cardStyle, inp }) {
  const [symbols, setSymbols] = React.useState(() => loadStorage(STORAGE_KEY, []));
  const [alerts, setAlerts] = React.useState(() => loadStorage(ALERTS_KEY, []));
  const [quotes, setQuotes] = React.useState({});
  const [inputSymbol, setInputSymbol] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [alertForm, setAlertForm] = React.useState({ symbol: '', direction: 'above', price: '' });
  const [showAlertForm, setShowAlertForm] = React.useState(false);
  const [firedAlerts, setFiredAlerts] = React.useState(new Set());

  // Persist
  React.useEffect(() => { localStorage.setItem(STORAGE_KEY, JSON.stringify(symbols)); }, [symbols]);
  React.useEffect(() => { localStorage.setItem(ALERTS_KEY, JSON.stringify(alerts)); }, [alerts]);

  // Fetch live quotes
  const fetchQuotes = React.useCallback(async () => {
    if (symbols.length === 0) { setQuotes({}); return; }
    try {
      const res = await fetch(`/api/gap-scanner?symbols=${encodeURIComponent(symbols.join(','))}`);
      const json = await res.json();
      const rows = json?.quoteResponse?.result || [];
      const next = {};
      rows.forEach(q => {
        next[q.symbol] = {
          price: toNum(q.regularMarketPrice, NaN),
          change: toNum(q.regularMarketChange, NaN),
          changePct: toNum(q.regularMarketChangePercent, NaN),
          high: toNum(q.regularMarketDayHigh, NaN),
          low: toNum(q.regularMarketDayLow, NaN),
          volume: q.regularMarketVolume,
          name: q.shortName || q.longName || q.symbol,
        };
      });
      setQuotes(next);
      return next;
    } catch { return null; }
  }, [symbols]);

  React.useEffect(() => {
    setLoading(true);
    fetchQuotes().finally(() => setLoading(false));
    const id = setInterval(fetchQuotes, 60000);
    return () => clearInterval(id);
  }, [fetchQuotes]);

  // Check alerts
  React.useEffect(() => {
    if (Object.keys(quotes).length === 0) return;
    alerts.forEach(alert => {
      if (firedAlerts.has(alert.id)) return;
      const q = quotes[alert.symbol];
      if (!q || isNaN(q.price)) return;
      const triggered =
        (alert.direction === 'above' && q.price >= toNum(alert.price)) ||
        (alert.direction === 'below' && q.price <= toNum(alert.price));
      if (triggered) {
        setFiredAlerts(prev => new Set([...prev, alert.id]));
        // Browser notification
        if (Notification.permission === 'granted') {
          new Notification(`🔔 Price Alert: ${alert.symbol}`, {
            body: `${alert.symbol} is ${alert.direction} $${alert.price} — now at $${q.price.toFixed(2)}`,
            icon: '/favicon.ico',
          });
        }
      }
    });
  }, [quotes, alerts, firedAlerts]);

  const addSymbol = () => {
    const s = inputSymbol.trim().toUpperCase();
    if (!s || symbols.includes(s)) return;
    setSymbols(prev => [...prev, s]);
    setInputSymbol('');
  };

  const removeSymbol = (s) => {
    setSymbols(prev => prev.filter(x => x !== s));
    setAlerts(prev => prev.filter(a => a.symbol !== s));
    setQuotes(prev => { const n = { ...prev }; delete n[s]; return n; });
  };

  const addAlert = () => {
    if (!alertForm.symbol || !alertForm.price) return;
    const newAlert = { id: Date.now(), ...alertForm, price: alertForm.price, created: new Date().toISOString() };
    setAlerts(prev => [...prev, newAlert]);
    setAlertForm({ symbol: alertForm.symbol, direction: 'above', price: '' });
    setShowAlertForm(false);
    // Request notification permission
    if (Notification.permission === 'default') Notification.requestPermission();
  };

  const removeAlert = (id) => setAlerts(prev => prev.filter(a => a.id !== id));

  const labelStyle = { color: C.sub, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

      {/* Add Symbol */}
      <div style={{ ...cardStyle, padding: '18px 20px' }}>
        <div style={{ ...labelStyle, marginBottom: 12 }}>Add to Watchlist</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={inputSymbol}
            onChange={e => setInputSymbol(e.target.value.toUpperCase())}
            onKeyDown={e => e.key === 'Enter' && addSymbol()}
            placeholder="AAPL, TSLA, BTC-USD..."
            style={{ ...inp, flex: 1 }}
          />
          <button onClick={addSymbol} style={{ padding: '10px 20px', background: C.blue, color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap' }}>
            + Add
          </button>
        </div>
      </div>

      {/* Watchlist Table */}
      <div style={{ ...cardStyle, padding: '18px 20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={labelStyle}>Watchlist {symbols.length > 0 && `· ${symbols.length} symbols`}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            {loading && <span style={{ color: C.sub, fontSize: 11 }}>Refreshing...</span>}
            <button onClick={() => { setLoading(true); fetchQuotes().finally(() => setLoading(false)); }}
              style={{ padding: '5px 12px', background: C.card2, border: `1px solid ${C.border}`, color: C.sub, borderRadius: 7, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
              ↻ Refresh
            </button>
            <button onClick={() => { setShowAlertForm(v => !v); }}
              style={{ padding: '5px 12px', background: `${C.orange}18`, border: `1px solid ${C.orange}44`, color: C.orange, borderRadius: 7, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
              🔔 Set Alert
            </button>
          </div>
        </div>

        {symbols.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px 0', color: C.sub, fontSize: 13 }}>
            No symbols yet — add tickers above to watch live prices
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {/* Header */}
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 110px 110px 90px 90px 36px', gap: 8, padding: '8px 12px', color: C.sub, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: `1px solid ${C.border}` }}>
              {['Symbol', 'Name', 'Price', 'Change', 'High', 'Low', ''].map(h => <div key={h}>{h}</div>)}
            </div>
            {symbols.map((sym, i) => {
              const q = quotes[sym];
              const hasData = q && !isNaN(q.price);
              const up = hasData && q.changePct >= 0;
              const alertsForSym = alerts.filter(a => a.symbol === sym);
              return (
                <div key={sym} style={{ borderBottom: i < symbols.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 110px 110px 90px 90px 36px', gap: 8, padding: '12px', alignItems: 'center', background: i % 2 === 0 ? 'transparent' : `${C.card2}55` }}>
                    <div style={{ fontWeight: 800, color: C.text, fontSize: 14 }}>{sym}</div>
                    <div style={{ color: C.sub, fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{hasData ? q.name : '—'}</div>
                    <div style={{ fontWeight: 800, color: C.text, fontSize: 14 }}>{hasData ? `$${q.price.toFixed(2)}` : '—'}</div>
                    <div style={{ color: hasData ? (up ? C.greenBright : C.redBright) : C.sub, fontWeight: 700, fontSize: 12 }}>
                      {hasData ? `${up ? '+' : ''}${q.changePct.toFixed(2)}%` : '—'}
                    </div>
                    <div style={{ color: C.sub, fontSize: 11 }}>{hasData ? `$${q.high.toFixed(2)}` : '—'}</div>
                    <div style={{ color: C.sub, fontSize: 11 }}>{hasData ? `$${q.low.toFixed(2)}` : '—'}</div>
                    <button onClick={() => removeSymbol(sym)} style={{ background: 'transparent', border: 'none', color: C.sub, cursor: 'pointer', fontSize: 16, padding: 0, lineHeight: 1 }}>×</button>
                  </div>
                  {/* Alerts for this symbol */}
                  {alertsForSym.length > 0 && (
                    <div style={{ padding: '4px 12px 10px', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {alertsForSym.map(a => {
                        const fired = firedAlerts.has(a.id);
                        return (
                          <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: fired ? `${C.greenBright}15` : `${C.orange}15`, border: `1px solid ${fired ? C.greenBright : C.orange}44`, color: fired ? C.greenBright : C.orange }}>
                            {fired ? '✓' : '🔔'} {a.direction === 'above' ? '↑' : '↓'} ${a.price}
                            <button onClick={() => removeAlert(a.id)} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 13, padding: 0, marginLeft: 2, opacity: 0.6 }}>×</button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Alert Form */}
      {showAlertForm && (
        <div style={{ ...cardStyle, padding: '18px 20px', border: `1px solid ${C.orange}44` }}>
          <div style={{ ...labelStyle, color: C.orange, marginBottom: 14 }}>🔔 New Price Alert</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 10, alignItems: 'flex-end' }}>
            <div>
              <div style={{ ...labelStyle, marginBottom: 6 }}>Symbol</div>
              <select value={alertForm.symbol} onChange={e => setAlertForm(f => ({ ...f, symbol: e.target.value }))} style={{ ...inp }}>
                <option value="">Select...</option>
                {symbols.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <div style={{ ...labelStyle, marginBottom: 6 }}>Condition</div>
              <select value={alertForm.direction} onChange={e => setAlertForm(f => ({ ...f, direction: e.target.value }))} style={{ ...inp }}>
                <option value="above">Price goes above</option>
                <option value="below">Price goes below</option>
              </select>
            </div>
            <div>
              <div style={{ ...labelStyle, marginBottom: 6 }}>Target Price ($)</div>
              <input type="number" value={alertForm.price} onChange={e => setAlertForm(f => ({ ...f, price: e.target.value }))} placeholder="0.00" style={{ ...inp }} />
            </div>
            <button onClick={addAlert} style={{ padding: '10px 18px', background: C.orange, border: 'none', borderRadius: 10, color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap' }}>
              Set Alert
            </button>
          </div>
          <div style={{ color: C.sub, fontSize: 11, marginTop: 10 }}>
            You'll receive a browser notification when the price condition is met (page must be open).
          </div>
        </div>
      )}

      {/* All Alerts Summary */}
      {alerts.length > 0 && (
        <div style={{ ...cardStyle, padding: '18px 20px' }}>
          <div style={{ ...labelStyle, marginBottom: 12 }}>Active Alerts ({alerts.length})</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {alerts.map(a => {
              const fired = firedAlerts.has(a.id);
              const q = quotes[a.symbol];
              const currentPrice = q && !isNaN(q.price) ? q.price : null;
              return (
                <div key={a.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderRadius: 10, background: fired ? `${C.greenBright}0f` : C.card2, border: `1px solid ${fired ? C.greenBright : C.border}33` }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <span style={{ fontSize: 16 }}>{fired ? '✅' : '🔔'}</span>
                    <div>
                      <div style={{ color: C.text, fontWeight: 700, fontSize: 13 }}>
                        {a.symbol} {a.direction === 'above' ? '↑ above' : '↓ below'} ${a.price}
                      </div>
                      <div style={{ color: C.sub, fontSize: 11 }}>
                        {fired ? 'Alert fired!' : currentPrice ? `Current: $${currentPrice.toFixed(2)}` : 'Waiting for price data...'}
                      </div>
                    </div>
                  </div>
                  <button onClick={() => removeAlert(a.id)} style={{ background: 'transparent', border: `1px solid ${C.border}`, color: C.sub, cursor: 'pointer', borderRadius: 7, padding: '4px 10px', fontSize: 11 }}>Remove</button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
